import React, { useState, useEffect, useRef } from 'react';
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogTrigger } from "@/components/ui/dialog";
import { ArrowLeft, Loader2, X, Crown, Shield, MessageCircle, Heart, Sparkles } from "lucide-react";
import { creatorSpaceApi, type CreatorProfile as CreatorProfileType, type CreatorStats } from '@/lib/api/creatorSpace';
import { useToast } from "@/components/ui/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { getCreatorAvatarUrl, getImageUrl } from '@/lib/utils/image';
import { isVideoUrl } from '@/lib/media';
import { CreatorTimeline } from './CreatorTimeline';
import { BlurredMedia } from '@/components/BlurredMedia';
import { CharacterProfileGallery } from '@/components/character-profile/CharacterProfileGallery';
import { StickyMiniGallery } from '@/components/StickyMiniGallery';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import { PageLayout } from '@/layouts';
import { VStack, HStack } from '@/components/core';

interface CreatorProfileProps {
  creatorId: number;
}

export const CreatorProfile: React.FC<CreatorProfileProps> = ({ creatorId }) => {
  const [creator, setCreator] = useState<CreatorProfileType | null>(null);
  const { t } = useTranslation(['creatorSpace', 'common']);
  const [stats, setStats] = useState<CreatorStats | null>(null);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [loading, setLoading] = useState(true);
  const [subscribing, setSubscribing] = useState(false);
  const [imageDialogOpen, setImageDialogOpen] = useState(false);
  const { toast } = useToast();
  const { user } = useAuth();
  const navigate = useNavigate();
  const ageVerified = user?.is_adult_verified || false;

  // Ref for the full gallery to track when it scrolls out of view
  const fullGalleryRef = useRef<HTMLDivElement>(null);

  const loadCreatorData = async () => {
    try {
      setLoading(true);
      const [creatorData, statsData] = await Promise.all([
        creatorSpaceApi.getCreator(creatorId),
        creatorSpaceApi.getCreatorStats(creatorId)
      ]);

      setCreator(creatorData);
      setStats(statsData);

      if (user) {
        const subscriptionStatus = await creatorSpaceApi.isSubscribed(creatorId);
        setIsSubscribed(subscriptionStatus);
      } else {
        setIsSubscribed(false);
      }
    } catch (error) {
      console.error('Error loading creator data:', error);
      toast({
        title: t('common:errors.error'),
        description: t('profile.loadError'),
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSubscribe = async () => {
    if (!creator) return;
    
    if (!user) {
      navigate('/auth?tab=login');
      return;
    }

    try {
      setSubscribing(true);
      if (isSubscribed) {
        await creatorSpaceApi.unsubscribeFromCreator(creatorId);
        setIsSubscribed(false);
        
        setCreator(prev => prev ? { 
          ...prev, 
          total_subscribers: Math.max(0, prev.total_subscribers - 1),
          display_subscribers: Math.max(0, (prev.display_subscribers ?? (prev.total_subscribers + (prev.simulated_subscribers || 0))) - 1)
        } : null);
        
        toast({
          title: t('subscription.ended'),
          description: t('subscription.endedDesc', { name: creator.character?.name || 'Creator' }),
        });
      } else {
        await creatorSpaceApi.subscribeToCreator(creatorId);
        setIsSubscribed(true);
        
        setCreator(prev => prev ? { 
          ...prev, 
          total_subscribers: prev.total_subscribers + 1,
          display_subscribers: (prev.display_subscribers ?? (prev.total_subscribers + (prev.simulated_subscribers || 0))) + 1
        } : null);
        
        toast({
          title: t('subscription.success'),
          description: t('subscription.successDesc', { name: creator.character?.name || 'Creator' }),
          variant: "default"
        });
      }
    } catch (error: any) {
      console.error('Subscription error:', error);
      
      let errorMessage = t('subscription.error');
      if (error?.response?.data?.detail) {
        if (error.response.data.detail.includes("Insufficient coins")) {
          errorMessage = t('subscription.errorCoins');
        } else if (error.response.data.detail.includes("already has active subscription")) {
          errorMessage = t('subscription.errorActive');
        } else {
          errorMessage = error.response.data.detail;
        }
      }
      
      toast({
        title: t('subscription.error'),
        description: errorMessage,
        variant: "destructive"
      });
    } finally {
      setSubscribing(false);
    }
  };

  useEffect(() => {
    void loadCreatorData();
  }, [creatorId, user]);

  // Loading State
  if (loading) {
    return (
      <div className="min-h-screen design-page-gradient">
        <div className="page-content px-token-md md:px-token-lg">
          <HStack align="center" justify="center" className="min-h-[50vh]">
            <div className="text-center fade-in-up">
              <Loader2 className="w-12 h-12 animate-spin mx-auto mb-token-md" style={{ color: 'hsl(var(--brand-primary))' }} />
              <p className="design-text-secondary">{t('profile.loading')}</p>
            </div>
          </HStack>
        </div>
      </div>
    );
  }

  // Not Found State
  if (!creator) {
    return (
      <div className="min-h-screen design-page-gradient">
        <div className="page-content px-token-md md:px-token-lg">
          <HStack align="center" justify="center" className="min-h-[50vh]">
            <div className="design-card p-token-xl max-w-md w-full text-center fade-in-up">
              <div className="w-16 h-16 bg-error/20 rounded-full flex items-center justify-center mx-auto mb-token-md">
                <X size={32} className="text-error" />
              </div>
              <h2 className="text-2xl font-bold design-text-primary mb-token-md">{t('profile.notFound')}</h2>
              <Button 
                onClick={() => navigate('/creator-space')}
                variant="gradient"
                className="w-full h-12 font-semibold"
              >
                <ArrowLeft className="h-4 w-4 mr-token-xs" />
                {t('profile.back')}
              </Button>
            </div>
          </HStack>
        </div>
      </div>
    );
  }

  // Paywall for non-authenticated users
  if (!user) {
    return (
      <div className="min-h-screen design-page-gradient">
        <div className="absolute top-1/4 left-1/4 w-64 h-64 bg-white/10 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-white/5 rounded-full blur-3xl pointer-events-none" />

        <div className="page-content first-content px-token-md md:px-token-lg">
          <div className="max-w-4xl mx-auto pb-16 text-center">
            <div className="design-card p-token-xl mb-token-xl fade-in-up">
              <div className="relative inline-block mb-token-lg">
                <div className="w-32 h-32 mx-auto rounded-full overflow-hidden ring-4 ring-white/20 shadow-token-modal">
                  <div className="w-full h-full bg-gradient-to-br from-brand-secondary to-brand-primary flex items-center justify-center">
                    <span className="text-3xl font-bold text-white">
                      {creator.character?.name?.[0] || 'C'}
                    </span>
                  </div>
                </div>
                {creator.is_verified && (
                  <Badge className="absolute bottom-0 right-0 bg-info border-0">
                    <Shield className="h-3 w-3" />
                  </Badge>
                )}
              </div>

              <h1 className="text-3xl font-bold design-text-primary mb-token-md">
                {creator.character?.name || t('profile.unknown')}
              </h1>
              
              <HStack align="center" justify="center" gap="md" className="mb-token-lg">
                <Badge variant="outline" className="px-token-sm py-token-xs border-white/20">
                  {creator.tier === 'basic' ? t('filter.tier.basic') : creator.tier === 'premium' ? t('filter.tier.premium') : t('filter.tier.exclusive')} Creator
                </Badge>
                <span className="design-text-secondary">
                  {(creator.display_subscribers ?? creator.total_subscribers)} {t('profile.subscribers')}
                </span>
              </HStack>

              <p className="design-text-secondary text-lg mb-token-xl max-w-2xl mx-auto leading-relaxed">
                {creator.character?.description || t('profile.exclusiveContent')}
              </p>

              <div className="bg-gradient-to-r from-brand-secondary/20 to-brand-primary/20 rounded-token-card p-token-lg mb-token-xl border border-white/10">
                <Crown className="h-12 w-12 mx-auto mb-token-md text-warning" />
                <h3 className="text-xl font-bold design-text-primary mb-token-xs">
                  {t('profile.exclusiveAccess')}
                </h3>
                <p className="design-text-secondary">
                  {t('profile.exclusiveDesc', { count: creator.total_posts })}
                </p>
              </div>

              <VStack gap="md" className="sm:flex-row justify-center">
                <Button
                  variant="gradient"
                  className="px-token-xl py-token-md text-lg font-bold"
                  onClick={() => navigate('/auth?tab=register')}
                >
                  <Crown className="h-5 w-5 mr-token-xs" />
                  {t('profile.register')}
                </Button>
                <Button
                  variant="glass"
                  className="px-token-xl py-token-md text-lg font-bold"
                  onClick={() => navigate('/auth?tab=login')}
                >
                  {t('profile.login')}
                </Button>
              </VStack>
            </div>

            <Button 
              variant="ghost"
              onClick={() => navigate('/creator-space')}
              className="text-white/70 hover:text-white"
            >
              <ArrowLeft className="h-4 w-4 mr-token-xs" />
              {t('profile.back')}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Get avatar URL
  const directAvatar = creator.character?.avatar_url ? getImageUrl(creator.character.avatar_url) : '';
  const directIsVideo = directAvatar ? isVideoUrl(directAvatar) : false;
  const avatarUrl = directIsVideo ? directAvatar : getCreatorAvatarUrl(creator, ageVerified);
  const profileImageData = creator.character?.gallery?.find(img => img.is_profile_image) || creator.character?.gallery?.[0];
  const isProfileImageNsfw = profileImageData?.is_fsk18 || false;
  const shouldBlurImage = profileImageData?.should_blur || false;
  const posterUrl = (profileImageData as any)?.thumbnail_url as string | undefined;
  
  // Gallery images for mini view
  const galleryImages = (creator.character?.gallery || [])
    .filter((img: any) => {
      const category = (img.category || '').toLowerCase();
      return category !== 'chat' && category !== 'creator' && !img.is_profile_image;
    })
    .slice(0, 8);

  const hasGallery = creator?.character && galleryImages.length > 0;

  // Authenticated user - Main Layout
  return (
    <PageLayout maxWidth="xl" padding="md" className="relative">
      {/* Background Effects */}
      <div className="absolute top-40 left-10 w-72 h-72 bg-brand-secondary/10 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute top-96 right-10 w-96 h-96 bg-brand-primary/8 rounded-full blur-3xl pointer-events-none" />

      {/* ===== STICKY MINI GALLERY ===== */}
      {hasGallery && creator.character && (
        <StickyMiniGallery
          name={creator.character.name || 'Creator'}
          galleryImages={galleryImages}
          character={{
            id: creator.character.id,
            name: creator.character.name,
            avatar_url: creator.character.avatar_url,
            gallery: creator.character.gallery || [],
          }}
          triggerRef={fullGalleryRef}
          showOnMobile={true}
        />
      )}

      {/* Back Button - Mobile only */}
      <HStack justify="between" align="center" className="md:hidden mb-token-md">
        <Button
          variant="ghost"
          onClick={() => navigate('/creator-space')}
          className="text-white/70 hover:text-white hover:bg-white/10 -ml-token-xs"
        >
          <ArrowLeft className="h-4 w-4 mr-token-xs" />
          {t('profile.back')}
        </Button>
      </HStack>

      {/* Main Grid Layout */}
      <div className="grid grid-cols-1 md:grid-cols-[280px_1fr] lg:grid-cols-[320px_1fr] gap-token-lg lg:gap-token-xl items-start pb-16">

        {/* ===== LEFT SIDEBAR - STICKY ===== */}
        <VStack as="aside" gap="md" className="md:sticky md:top-[1.5rem] self-start">

          {/* Hero Image Card (like CharacterProfile) */}
          <div className={cn(
            "design-card overflow-hidden relative",
            creator.tier === 'premium' && "ring-1 ring-warning/30",
            creator.tier === 'exclusive' && "ring-1 ring-brand-secondary/30"
          )}>
            {/* Hero Image */}
            <div className="aspect-[4/5] relative">
              {avatarUrl ? (
                <Dialog open={imageDialogOpen} onOpenChange={setImageDialogOpen}>
                  <DialogTrigger asChild>
                    <div className="w-full h-full cursor-pointer hover:opacity-95 transition-opacity">
                      <BlurredMedia
                        src={avatarUrl}
                        alt={creator.character?.name || 'Creator'}
                        shouldBlur={shouldBlurImage}
                        isFsk18={isProfileImageNsfw}
                        containerClassName="w-full h-full"
                        className="w-full h-full object-cover object-top"
                        autoPlayVideo
                        showMuteToggle={false}
                        loopVideo={false}
                        {...((directIsVideo || isVideoUrl(avatarUrl)) ? { forceVideo: true } : {})}
                        {...(posterUrl ? { poster: posterUrl } : {})}
                      />
                    </div>
                  </DialogTrigger>
                  <DialogContent className="max-w-4xl w-[95vw] max-h-[95vh] p-0 bg-black/95 border-white/10">
                    <button
                      onClick={() => setImageDialogOpen(false)}
                      className="absolute top-token-md right-token-md z-50 p-token-xs rounded-full bg-black/50 hover:bg-black/70 transition-colors"
                    >
                      <X className="h-6 w-6 text-white" />
                    </button>
                    <div className="flex items-center justify-center w-full h-full p-token-md">
                      <BlurredMedia
                        src={avatarUrl}
                        alt={creator.character?.name || 'Creator'}
                        shouldBlur={shouldBlurImage}
                        isFsk18={isProfileImageNsfw}
                        className="max-w-full max-h-[85vh] object-contain rounded-token-card"
                      />
                    </div>
                  </DialogContent>
                </Dialog>
              ) : (
                <div className="w-full h-full bg-gradient-to-br from-brand-secondary to-brand-primary flex items-center justify-center">
                  <span className="text-6xl font-bold text-white">
                    {creator.character?.name?.[0] || 'C'}
                  </span>
                </div>
              )}

              {/* Gradient Overlay */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent pointer-events-none" />

              {/* Badges (top right) */}
              <VStack gap="xs" className="absolute top-3 right-3 z-[2]">
                {creator.tier !== 'basic' && (
                  <Badge className={cn(
                    "px-token-xs py-token-xs text-xs font-semibold",
                    creator.tier === 'premium'
                      ? "bg-warning/90 text-warning-foreground border-0"
                      : "bg-brand-secondary/90 text-white border-0"
                  )}>
                    <Crown className="h-3 w-3 mr-token-xs" />
                    {creator.tier === 'premium' ? t('filter.tier.premium') : t('filter.tier.exclusive')}
                  </Badge>
                )}
                {creator.is_verified && (
                  <Badge className="bg-info/90 text-white border-0 px-token-xs py-token-xs">
                    <Shield className="h-3 w-3 mr-token-xs" />
                    Verified
                  </Badge>
                )}
              </VStack>

              {/* Name + Stats Overlay (bottom) */}
              <div className="absolute bottom-0 left-0 right-0 p-token-md z-[2]">
                <HStack align="center" gap="sm" className="mb-token-xs">
                  <h1 className="text-xl font-bold text-white drop-shadow-token-card">
                    {creator.character?.name || t('profile.unknown')}
                  </h1>
                </HStack>
                <HStack align="center" gap="sm" className="text-white/80 text-sm">
                  <span>{creator.display_subscribers ?? creator.total_subscribers} {t('profile.subscribers')}</span>
                  <span>•</span>
                  <span>{creator.total_posts} Posts</span>
                </HStack>
              </div>
            </div>
          </div>

          {/* ===== MOBILE ACTION BUTTONS - Enhanced with prominent styling ===== */}
          <HStack gap="md" wrap className="md:hidden">
            <Button
              onClick={handleSubscribe}
              disabled={subscribing}
              variant={isSubscribed ? "glass" : "gradient-primary"}
              className={cn(
                "flex-1 h-14 text-lg font-semibold",
                !isSubscribed && "btn-profile-action"
              )}
              data-subscribe-button
            >
              {subscribing ? (
                <Loader2 className="h-5 w-5 animate-spin" />
              ) : isSubscribed ? (
                <>
                  <Heart className="h-5 w-5 mr-token-xs fill-current" />
                  {t('profile.subscribed')}
                </>
              ) : (
                <>
                  <Sparkles className="h-5 w-5 mr-token-xs" />
                  {t('profile.subscribe', { price: creator.monthly_price })}
                </>
              )}
            </Button>

            <Button
              onClick={() => navigate(`/chats/${creator.character_id}`)}
              variant="glass"
              className="flex-1 h-14 text-lg font-semibold"
            >
              <MessageCircle className="h-5 w-5 mr-token-xs" />
              Chat
            </Button>
          </HStack>

          {/* About Card (Desktop) */}
          <div className="design-card p-token-md hidden md:block">
            <h3 className="text-sm font-semibold design-text-primary mb-token-sm">{t('profile.about', 'Über')}</h3>
            <p className="design-text-secondary text-sm leading-relaxed">
              {creator.character?.description || t('profile.noDescription')}
            </p>

            {/* Stats Row */}
            <HStack justify="between" gap="md" className="py-token-sm mt-token-md border-t border-white/10">
              <div className="text-center flex-1">
                <div className="text-lg font-bold design-text-primary">
                  {creator.display_subscribers ?? creator.total_subscribers}
                </div>
                <div className="text-[10px] design-text-secondary uppercase tracking-wide">{t('profile.subscribers')}</div>
              </div>
              <div className="text-center flex-1">
                <div className="text-lg font-bold design-text-primary">
                  {creator.total_posts}
                </div>
                <div className="text-[10px] design-text-secondary uppercase tracking-wide">Posts</div>
              </div>
              {stats && (
                <div className="text-center flex-1">
                  <div className="text-lg font-bold design-text-primary">
                    {stats.average_likes_per_post.toFixed(1)}
                  </div>
                  <div className="text-[10px] design-text-secondary uppercase tracking-wide">Ø Likes</div>
                </div>
              )}
            </HStack>

            {/* Price */}
            <HStack align="center" justify="between" className="py-token-xs px-token-sm mt-token-sm rounded-token-button bg-gradient-to-r from-warning/10 to-warning/5 border border-warning/20">
              <span className="text-xs design-text-secondary">Monatspreis</span>
              <span className="text-base font-bold text-warning">
                {Math.round(creator.monthly_price)} Coins
              </span>
            </HStack>

            {/* Desktop Subscribe Button - Enhanced */}
            <VStack gap="sm" className="mt-token-sm">
              <Button
                onClick={handleSubscribe}
                disabled={subscribing}
                variant={isSubscribed ? "glass" : "gradient-primary"}
                className={cn(
                  "w-full h-11 font-semibold",
                  !isSubscribed && "btn-profile-action"
                )}
                data-subscribe-button
              >
                {subscribing ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : isSubscribed ? (
                  <>
                    <Heart className="h-4 w-4 mr-token-xs fill-current" />
                    {t('profile.subscribed')}
                  </>
                ) : (
                  <>
                    <Sparkles className="h-4 w-4 mr-token-xs" />
                    {t('profile.subscribe', { price: creator.monthly_price })}
                  </>
                )}
              </Button>

              <Button
                onClick={() => navigate(`/chats/${creator.character_id}`)}
                variant="glass"
                className="w-full h-11 font-semibold"
              >
                <MessageCircle className="h-4 w-4 mr-token-xs" />
                {t('profile.chat', { name: creator.character?.name })}
              </Button>
            </VStack>
          </div>

          {/* Back to Creator Space - Desktop only */}
          <HStack justify="start" className="hidden md:flex">
            <Button
              variant="ghost"
              onClick={() => navigate('/creator-space')}
              className="text-white/50 hover:text-white hover:bg-white/10 justify-start text-sm h-9"
            >
              <ArrowLeft className="h-4 w-4 mr-token-xs" />
              {t('profile.back')}
            </Button>
          </HStack>
        </VStack>

        {/* ===== RIGHT CONTENT ===== */}
        <VStack as="main" gap="lg" className="min-w-0 pt-token-xs md:pt-0">

          {/* Full Gallery (scrolls normally) */}
          {hasGallery && (
            <div ref={fullGalleryRef}>
              <CharacterProfileGallery
                character={{
                  id: creator.character!.id,
                  name: creator.character!.name,
                  avatar_url: creator.character!.avatar_url,
                  gallery: (creator.character!.gallery || []).map((img: any) => ({
                    id: img.id,
                    url: img.url,
                    thumbnail_url: img.thumbnail_url,
                    media_type: img.media_type,
                    category: img.category ?? 'public',
                    title: img.title,
                    description: img.description,
                    is_premium: Boolean(img.is_premium),
                    is_fsk18: Boolean(img.is_fsk18),
                    is_profile_image: Boolean(img.is_profile_image),
                    sort_order: img.sort_order,
                    unlock_price: img.unlock_price,
                    should_blur: Boolean(img.should_blur),
                  }))
                } as any}
                fullHeight={false}
              />
            </div>
          )}

          {/* Timeline */}
          <CreatorTimeline
            creatorId={creatorId}
            maxWidth="w-full max-w-2xl"
            title={`${creator.character?.name || 'Creator'} Timeline`}
          />
        </VStack>
      </div>
    </PageLayout>
  );
};
